import { checkStatus } from '../../utilities/fetch';
import { newslabNewsletterPath } from '../../utilities/api_relative_paths';

/**
 * @param {number} id - newsletter id
 * @returns {Promise} Promise that can be extended
 */
const getNewsletter = (id) => {
  if (!id) return Promise.reject('Missing params');

  const url = `${newslabNewsletterPath}/${id}/subscriptions`;

  return fetch(url)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * Returns a list of newsletters that the user can subscribe to and are related to a given article
 *
 * @param {string} articleId - article id
 * @returns {Promise} Promise that can be extended
 */
const getNewsletterForArticle = (articleId) => {
  if (!articleId) return Promise.reject('Missing params');

  return fetch(`${newslabNewsletterPath}/for_article/${articleId}`)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * Given a newsletter ID:
 * - subscribes current user to newsletter (used for AAs)
 * or
 * - subscribes passed email to newsletter (used for UAs)
 *
 * @param {object} newsletter - Newsletter containing properties
 * @param {number} newsletter.id - ID of newsletter to subscribe to
 * @param {string} newsletter.permalink - Permalink of newsletter to subscribe to
 * @param {object} payload - Post request payload
 * @param {string} [payload.email] - Email of the UA to unsubscribe the newsletter from
 * @param {string} payload.referrer - Ref. to from un-subscription is triggered: url#newsletter
 * @returns {Promise} Promise that can be extended
 */
const subscribeToNewsletter = ({id, permalink}, payload) => {
  if (!id) return Promise.reject('Wrong params');

  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const plausibleTrackingFunction = (response) => {
    if (typeof window.plausible !== 'function') {
      return response;
    }

    window.plausible('newsletter_subscription', { props: { newsletter: permalink } });
    return response;
  };

  return fetch(`${newslabNewsletterPath}/${id}/subscriptions`, requestOptions)
    .then(checkStatus)
    .then(plausibleTrackingFunction)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

/**
 * Given a newsletter ID:
 * - unsubscribes current user from newsletter (used for AAs)
 * or
 * - unsubscribes passed email from newsletter (used for UAs)
 *
 * @param {number} newsletterId - ID of newsletter to unsubscribe from
 * @param {object} payload - Delete request payload
 * @param {string} [payload.email] - Email of the UA to unsubscribe the newsletter from
 * @param {string} [payload.referrer] - Ref. to from un-subscription is triggered: url#newsletter
 * @returns {Promise} Promise that can be extended
 */
const unsubscribeFromNewsletter = (newsletterId, payload) => {
  if (!newsletterId) return Promise.reject('Wrong params');

  const requestOptions = {
    method: 'DELETE',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return fetch(`${newslabNewsletterPath}/${newsletterId}/subscriptions`, requestOptions)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => Promise.reject(error));
};

export {
  getNewsletter,
  getNewsletterForArticle,
  subscribeToNewsletter,
  unsubscribeFromNewsletter,
};
